import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  CondolencesIntegration,
  CondolencesIntegrationInput,
  CondolencesLoginInput,
  CondolencesOrdersRefresh,
  CondolencesTokenCheck,
} from './condolences.types'

/** Calls that read or change the organization's Condolences connection. */
interface CondolencesTarget {
  organizationId: OrganizationId
}

/** The query key's entries; the scoped key builder always sets the organization. */
export type CondolencesQueryKeyEntries = CondolencesTarget

/** Query functions read the key, and the abort signal when the caller passes one. */
type CondolencesQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'> &
  Partial<Pick<QueryFunctionContext<[TKey]>, 'signal'>>

export async function getCondolencesIntegration({
  queryKey: [{ organizationId }],
  signal,
}: CondolencesQueryContext<CondolencesQueryKeyEntries>) {
  return (
    (await axios.get<CondolencesIntegration>(
      '/condolences/integration',
      buildAdminRequestConfig({
        organizationId,
        ...(signal ? { signal } : {}),
      })
    )) || {}
  )
}

export async function updateCondolencesIntegration({
  organizationId,
  ...values
}: CondolencesTarget & CondolencesIntegrationInput) {
  return axios.put<CondolencesIntegration>(
    '/condolences/integration',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function connectCondolencesWithLogin({
  organizationId,
  email,
  password,
}: CondolencesTarget & CondolencesLoginInput) {
  return axios.post<CondolencesIntegration>(
    '/condolences/token/connect',
    { email, password },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function checkCondolencesToken({ organizationId }: CondolencesTarget) {
  return axios.post<CondolencesTokenCheck>(
    '/condolences/token/check',
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function refreshCondolencesOrders({ organizationId }: CondolencesTarget) {
  return axios.post<CondolencesOrdersRefresh>(
    '/condolences/orders/refresh',
    null,
    buildAdminRequestConfig({ organizationId })
  )
}
